// The board document: a flat map id → BoardObject. Objects are immutable; an
// update replaces the map entry. Every applied op yields its inverse, which is
// what undo/redo (and later, rollback of rejected ops) is built on.
import type { BoardObject, ChecklistItem, Op } from '../../shared/types.ts';
import { applyUpdate, opObjectId } from '../../shared/ops.ts';
import { applyItemOp } from '../../shared/cards.ts';

export type ChangeListener = (changed: ReadonlySet<string>) => void;

export class Store {
  private readonly objects = new Map<string, BoardObject>();
  private readonly listeners = new Set<ChangeListener>();
  private order: string[] = [];
  private ranks = new Map<string, number>();
  private orderDirty = false;

  get size(): number { return this.objects.size; }
  get(id: string): BoardObject | undefined { return this.objects.get(id); }
  has(id: string): boolean { return this.objects.has(id); }
  values(): IterableIterator<BoardObject> { return this.objects.values(); }

  subscribe(fn: ChangeListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Applies ops in order and returns the inverse of each op that took effect,
   * in application order. Ops that can't apply (update/delete of a missing
   * object, create of an existing id) are skipped silently.
   */
  apply(ops: readonly Op[]): Op[] {
    return this.applyTracked(ops).inverses;
  }

  /** Like apply(), but also returns the ops that took effect (what gets synced). */
  applyTracked(ops: readonly Op[]): { applied: Op[]; inverses: Op[] } {
    const applied: Op[] = [];
    const inverses: Op[] = [];
    const changed = new Set<string>();
    for (const op of ops) {
      const inv = this.applyOne(op);
      if (!inv) continue;
      applied.push(op);
      inverses.push(inv);
      changed.add(opObjectId(op));
    }
    this.emit(changed);
    return { applied, inverses };
  }

  /**
   * Sets objects to exact values (undefined = absent) without producing ops.
   * Used to reconcile with the server; never recorded in history.
   */
  setMany(entries: Iterable<[string, BoardObject | undefined]>): void {
    const changed = new Set<string>();
    for (const [id, obj] of entries) {
      const old = this.objects.get(id);
      if (old === obj) continue;
      if (obj) this.objects.set(id, obj);
      else if (old) this.objects.delete(id);
      else continue;
      if (!old || !obj || old.z !== obj.z) this.orderDirty = true;
      changed.add(id);
    }
    this.emit(changed);
  }

  /** Replaces the whole document (e.g. a fresh server snapshot). */
  replaceAll(objects: readonly BoardObject[]): void {
    const next = new Map(objects.map((o) => [o.id, o]));
    const entries: [string, BoardObject | undefined][] = [];
    for (const id of this.objects.keys()) if (!next.has(id)) entries.push([id, undefined]);
    for (const [id, o] of next) entries.push([id, o]);
    this.setMany(entries);
  }

  private emit(changed: Set<string>): void {
    if (changed.size) for (const fn of this.listeners) fn(changed);
  }

  private applyOne(op: Op): Op | null {
    switch (op.t) {
      case 'create': {
        if (this.objects.has(op.obj.id)) return null;
        this.objects.set(op.obj.id, op.obj);
        this.orderDirty = true;
        return { t: 'delete', id: op.obj.id };
      }
      case 'update': {
        const old = this.objects.get(op.id);
        if (!old) return null;
        const prev: Record<string, unknown> = {};
        const oldRec = old as unknown as Record<string, unknown>;
        for (const key of Object.keys(op.props)) {
          if (key === 'id' || key === 'type') continue;
          prev[key] = oldRec[key];
        }
        this.objects.set(op.id, applyUpdate(old, op.props));
        if ('z' in op.props) this.orderDirty = true;
        return { t: 'update', id: op.id, props: prev as Partial<BoardObject> };
      }
      case 'delete': {
        const old = this.objects.get(op.id);
        if (!old) return null;
        this.objects.delete(op.id);
        this.orderDirty = true;
        return { t: 'create', obj: old };
      }
      case 'item': {
        const old = this.objects.get(op.id);
        if (old?.type !== 'card') return null;
        const next = applyItemOp(old, op.itemId, op.props);
        if (next === old) return null;
        this.objects.set(op.id, next);
        const prev = Object.hasOwn(old.items, op.itemId) ? old.items[op.itemId] : undefined;
        const { id, itemId } = op;
        // Created → remove it; removed → recreate it; edited → the fields' previous values.
        if (!prev) return { t: 'item', id, itemId, props: null };
        if (!op.props) return { t: 'item', id, itemId, props: { ...prev } };
        const props: Record<string, unknown> = {};
        for (const k of Object.keys(op.props)) props[k] = (prev as unknown as Record<string, unknown>)[k];
        return { t: 'item', id, itemId, props: props as Partial<ChecklistItem> };
      }
    }
  }

  /** Ids in paint order (ascending z, ties broken by id). */
  orderedIds(): readonly string[] {
    this.ensureOrder();
    return this.order;
  }

  /** Position in paint order; -1 if absent. */
  rank(id: string): number {
    this.ensureOrder();
    return this.ranks.get(id) ?? -1;
  }

  topZ(): string | null {
    const order = this.orderedIds();
    return order.length ? this.objects.get(order[order.length - 1]!)!.z : null;
  }

  bottomZ(): string | null {
    const order = this.orderedIds();
    return order.length ? this.objects.get(order[0]!)!.z : null;
  }

  private ensureOrder(): void {
    if (!this.orderDirty) return;
    const objs = [...this.objects.values()];
    objs.sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : 1));
    this.order = objs.map((o) => o.id);
    this.ranks = new Map(this.order.map((id, i) => [id, i]));
    this.orderDirty = false;
  }
}
